// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0
import { Alert, Badge, Button, Card, Group, Loader, Select, Stack, Text, Title } from '@mantine/core';
import { formatHumanName } from '@medplum/core';
import type { Patient, Practitioner } from '@medplum/fhirtypes';
import { useMedplum } from '@medplum/react';
import {
  type AppointmentTypeDefinition,
  type AvailableTimeSlot,
  resolveDefaultStaffPractitioner,
  type SchedulingEligibility,
} from '@polarissleep/scheduling';
import { IconCalendarCheck } from '@tabler/icons-react';
import type { JSX } from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { getProviderAppEnvConfig } from '../../config/env';
import type { ActivePractitionerOption } from '../../hooks/useActivePractitioners';
import { useActivePractitioners } from '../../hooks/useActivePractitioners';
import type { ProviderAppointmentCandidate } from '../../scheduling-next/types';
import { bookAppointment } from '../../services/booking-service';
import { bookProviderV2Appointment } from '../../services/booking-service-v2';
import { formatAppointmentDate } from '../../utils/appointment-formatting';
import { AppointmentTimeWithTimezone } from '../appointments/AppointmentTimeWithTimezone';
import { BookingConfirmation } from './BookingConfirmation';
import { SlotPicker } from './SlotPicker';

type BookingStep = 'type-selection' | 'slot-selection' | 'success';

interface BookingFlowProps {
  patient: Patient;
  eligibility: SchedulingEligibility;
  onSuccess: () => void;
  onCancel: () => void;
}

export function BookingFlow({ patient, eligibility, onSuccess, onCancel }: BookingFlowProps): JSX.Element {
  const medplum = useMedplum();
  const [step, setStep] = useState<BookingStep>('type-selection');
  const [selectedType, setSelectedType] = useState<AppointmentTypeDefinition | undefined>();
  const [selectedSlot, setSelectedSlot] = useState<AvailableTimeSlot | undefined>();
  const [selectedCandidate, setSelectedCandidate] = useState<ProviderAppointmentCandidate | undefined>();
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | undefined>();
  const [bookedRequiresPayment, setBookedRequiresPayment] = useState(false);
  const [selectedPractitionerId, setSelectedPractitionerId] = useState<string | null>(null);
  const [defaultStaffWarning, setDefaultStaffWarning] = useState<string | undefined>();
  const [resolvingDefaultStaff, setResolvingDefaultStaff] = useState(false);
  const [resolvedDefaultPractitioner, setResolvedDefaultPractitioner] = useState<Practitioner | undefined>();
  const [v2SearchReady, setV2SearchReady] = useState(false);
  const [v2SearchError, setV2SearchError] = useState<Error | null>(null);
  const { practitioners, loading: practitionersLoading, error: practitionersError } = useActivePractitioners();
  const duplicateTypeCodes = useMemo(
    () => new Set(eligibility.duplicateTypeCodes ?? []),
    [eligibility.duplicateTypeCodes]
  );
  const selectedTypeIsDuplicate = duplicateTypeCodes.has(selectedType?.coding.code ?? '');
  const practitionerOptions = useMemo(() => {
    if (!resolvedDefaultPractitioner?.id) {
      return practitioners;
    }

    const alreadyPresent = practitioners.some((option) => option.value === resolvedDefaultPractitioner.id);
    if (alreadyPresent) {
      return practitioners;
    }

    const fallbackOption: ActivePractitionerOption = {
      value: resolvedDefaultPractitioner.id,
      label: resolvedDefaultPractitioner.name?.[0]
        ? formatHumanName(resolvedDefaultPractitioner.name[0])
        : 'Unnamed practitioner',
      practitioner: resolvedDefaultPractitioner,
    };

    return [...practitioners, fallbackOption].sort((left, right) => left.label.localeCompare(right.label));
  }, [practitioners, resolvedDefaultPractitioner]);

  useEffect(() => {
    if (eligibility.allowedAppointmentTypes.length === 1) {
      setSelectedType(eligibility.allowedAppointmentTypes[0]);
      setStep('slot-selection');
    }
  }, [eligibility.allowedAppointmentTypes]);

  useEffect(() => {
    if (!selectedType) {
      return;
    }

    let active = true;
    setSelectedPractitionerId(null);
    setSelectedSlot(undefined);
    setSelectedCandidate(undefined);
    setResolvedDefaultPractitioner(undefined);
    setV2SearchReady(false);
    setV2SearchError(null);

    if (getProviderAppEnvConfig().schedulingEngine === 'medplum-v2') {
      return () => {
        active = false;
      };
    }

    setResolvingDefaultStaff(true);
    setDefaultStaffWarning(undefined);
    resolveDefaultStaffPractitioner(medplum, selectedType.coding.code ?? '')
      .then((result) => {
        if (!active) {
          return;
        }

        if (result.status === 'resolved') {
          setResolvedDefaultPractitioner(result.practitioner);
          setSelectedPractitionerId(result.practitionerId);
        } else {
          setDefaultStaffWarning(
            'Default staff is not configured correctly for this appointment type. Select a staff member to continue.'
          );
        }
      })
      .catch(() => {
        if (active) {
          setDefaultStaffWarning(
            'Default staff is not configured correctly for this appointment type. Select a staff member to continue.'
          );
        }
      })
      .finally(() => {
        if (active) {
          setResolvingDefaultStaff(false);
        }
      });

    return () => {
      active = false;
    };
  }, [medplum, selectedType]);

  const handleV2SearchStateChange = useCallback(({ ready, error }: { ready: boolean; error: Error | null }) => {
    setV2SearchReady(ready);
    setV2SearchError(error);
    if (!ready || error) {
      setSelectedCandidate(undefined);
      setShowConfirmation(false);
    }
  }, []);

  function handleTypeSelected(type: AppointmentTypeDefinition): void {
    setSelectedType(type);
    setSelectedSlot(undefined);
    setSelectedCandidate(undefined);
    setSelectedPractitionerId(null);
    setResolvedDefaultPractitioner(undefined);
    setDefaultStaffWarning(undefined);
    setStep('slot-selection');
  }

  async function handleConfirm(): Promise<void> {
    const isV2 = getProviderAppEnvConfig().schedulingEngine === 'medplum-v2';
    if (!selectedType || !patient.id || (isV2 ? !selectedCandidate : !selectedSlot)) {
      return;
    }

    setIsSubmitting(true);
    setError(undefined);

    try {
      const result =
        isV2 && selectedCandidate
          ? await bookProviderV2Appointment(medplum, {
              patient,
              candidate: selectedCandidate,
              appointmentType: selectedType,
            })
          : await bookAppointment(medplum, {
              patient,
              slotId: selectedSlot?.slotId ?? '',
              appointmentTypeCode: selectedType.coding.code ?? '',
              providerId: selectedSlot?.providerId ?? '',
              start: selectedSlot?.start ?? '',
              end: selectedSlot?.end ?? '',
              appointmentType: selectedType,
            });

      setShowConfirmation(false);
      setBookedRequiresPayment(result.requiresPayment);
      setStep('success');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setIsSubmitting(false);
    }
  }

  if (step === 'type-selection') {
    return (
      <Stack gap="md">
        <Title order={3}>Select Appointment Type</Title>
        <Text size="sm" c="dimmed">
          Based on the patient's care stage, the following appointment types are available:
        </Text>

        {eligibility.allowedAppointmentTypes.map((type) => (
          <Card
            key={type.coding.code}
            withBorder
            p="md"
            radius="md"
            style={{ cursor: 'pointer' }}
            onClick={() => handleTypeSelected(type)}
          >
            <Group justify="space-between">
              <Stack gap={4}>
                <Group gap="xs">
                  <Text fw={500}>{type.display}</Text>
                  {duplicateTypeCodes.has(type.coding.code ?? '') && (
                    <Badge color="yellow" variant="light" size="sm">
                      Already scheduled
                    </Badge>
                  )}
                </Group>
                <Text size="sm" c="dimmed">
                  {type.durationMinutes} min • {type.mode === 'virtual' ? 'Virtual' : 'In-Person'}
                </Text>
              </Stack>
              <Button variant="light" size="sm">
                Select
              </Button>
            </Group>
          </Card>
        ))}
      </Stack>
    );
  }

  if (step === 'slot-selection' && selectedType) {
    const selectedBooking = selectedCandidate ?? selectedSlot;
    return (
      <Stack gap="lg">
        {error && (
          <Alert color="red" title="Booking failed">
            {error}
          </Alert>
        )}

        {defaultStaffWarning && (
          <Alert color="yellow" title="Staff selection required">
            {defaultStaffWarning}
          </Alert>
        )}

        {practitionersError && getProviderAppEnvConfig().schedulingEngine === 'legacy' && (
          <Alert color="red" title="Could not load staff">
            {practitionersError.message}
          </Alert>
        )}

        {getProviderAppEnvConfig().schedulingEngine === 'legacy' && (
          <>
            <Select
              label="Staff"
              placeholder={practitionersLoading || resolvingDefaultStaff ? 'Loading staff…' : 'Select staff'}
              data={practitionerOptions}
              value={selectedPractitionerId}
              onChange={(value) => {
                setSelectedPractitionerId(value);
                setSelectedSlot(undefined);
                setSelectedCandidate(undefined);
              }}
              disabled={practitionersLoading || resolvingDefaultStaff || isSubmitting}
            />

            {(practitionersLoading || resolvingDefaultStaff) && <Loader size="sm" />}
          </>
        )}

        {selectedPractitionerId && getProviderAppEnvConfig().schedulingEngine === 'legacy' && (
          <SlotPicker
            appointmentType={selectedType}
            practitionerId={selectedPractitionerId}
            selectedSlot={selectedSlot}
            onSlotSelected={setSelectedSlot}
          />
        )}

        {getProviderAppEnvConfig().schedulingEngine === 'medplum-v2' && (
          <SlotPicker
            engine="medplum-v2"
            appointmentType={selectedType}
            selectedSlot={selectedCandidate}
            onSlotSelected={setSelectedCandidate}
            onSearchStateChange={handleV2SearchStateChange}
          />
        )}

        <Group justify="center" mt="md">
          <Button
            variant="outline"
            onClick={() => {
              if (eligibility.allowedAppointmentTypes.length > 1) {
                setSelectedType(undefined);
                setSelectedSlot(undefined);
                setSelectedCandidate(undefined);
                setSelectedPractitionerId(null);
                setResolvedDefaultPractitioner(undefined);
                setDefaultStaffWarning(undefined);
                setStep('type-selection');
              } else {
                onCancel();
              }
            }}
          >
            Back
          </Button>
          <Button
            disabled={
              getProviderAppEnvConfig().schedulingEngine === 'medplum-v2'
                ? !selectedCandidate || !v2SearchReady || Boolean(v2SearchError)
                : !selectedSlot
            }
            onClick={() => setShowConfirmation(true)}
          >
            Book Appointment
          </Button>
        </Group>

        {selectedBooking && (
          <BookingConfirmation
            opened={showConfirmation}
            appointmentType={selectedType}
            slot={selectedBooking}
            onConfirm={handleConfirm}
            onBack={() => setShowConfirmation(false)}
            isSubmitting={isSubmitting}
            isDuplicateType={selectedTypeIsDuplicate}
          />
        )}
      </Stack>
    );
  }

  const successSlot = selectedCandidate
    ? {
        start: selectedCandidate.appointment.start ?? '',
        end: selectedCandidate.appointment.end ?? '',
        providerName: selectedCandidate.providerName,
      }
    : selectedSlot;
  if (step === 'success' && selectedType && successSlot) {
    return (
      <Stack gap="md" align="center" py="xl">
        <IconCalendarCheck size={48} color="var(--mantine-color-green-6)" />
        <Title order={3}>Appointment Booked!</Title>
        {bookedRequiresPayment && (
          <Text size="sm" c="orange" ta="center">
            This appointment type requires upfront payment. The patient will receive a notification to complete payment
            in the patient app.
          </Text>
        )}
        <Card withBorder p="lg" radius="md" maw={400} w="100%">
          <Stack gap="sm">
            <Group justify="space-between">
              <Text size="sm" c="dimmed">
                Type
              </Text>
              <Text size="sm">{selectedType.display}</Text>
            </Group>
            <Group justify="space-between">
              <Text size="sm" c="dimmed">
                Date
              </Text>
              <Text size="sm">{formatAppointmentDate(successSlot.start)}</Text>
            </Group>
            <Group justify="space-between">
              <Text size="sm" c="dimmed">
                Time
              </Text>
              <AppointmentTimeWithTimezone start={successSlot.start} end={successSlot.end} textProps={{ size: 'sm' }} />
            </Group>
            <Group justify="space-between">
              <Text size="sm" c="dimmed">
                Provider
              </Text>
              <Text size="sm">{successSlot.providerName}</Text>
            </Group>
          </Stack>
        </Card>
        <Text size="sm" c="dimmed" ta="center">
          {selectedType.mode === 'virtual'
            ? 'A Zoom meeting link will be available to the patient before the appointment.'
            : 'Please remind the patient to arrive 10 minutes early.'}
        </Text>
        <Button variant="outline" onClick={onSuccess}>
          Back to Appointments
        </Button>
      </Stack>
    );
  }

  return <Text>Loading...</Text>;
}
